import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface MentorEffectivenessAnalysisChartProps {
  data: Array<{
    mentor_name: string;
    workload_score: number;
    success_rate: number;
    mentee_satisfaction: number;
    workshop_count: number;
    active_mentees: number;
    effectiveness_score: number;
  }>;
  loading?: boolean;
}

const MentorEffectivenessAnalysisChart: React.FC<MentorEffectivenessAnalysisChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mentor Effectiveness Analysis</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mentor Effectiveness Analysis</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No mentor effectiveness data available
        </div>
      </div>
    );
  }

  const getEffectivenessLevel = (score: number) => {
    if (score >= 80) return { level: 'Excellent', color: '#10b981' };
    if (score >= 60) return { level: 'Good', color: '#3b82f6' };
    if (score >= 40) return { level: 'Average', color: '#f59e0b' };
    return { level: 'Needs Improvement', color: '#ef4444' };
  };

  const getMentorColor = (index: number) => {
    const colors = [
      '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
      '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Mentor Effectiveness Analysis</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              dataKey="workload_score" 
              name="Workload Score"
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <YAxis 
              type="number" 
              dataKey="success_rate" 
              name="Success Rate (%)"
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              formatter={(value: number, name: string, props: any) => {
                if (name === 'workload_score') return [`${value.toFixed(1)}%`, 'Workload Score'];
                if (name === 'success_rate') return [`${value.toFixed(1)}%`, 'Success Rate'];
                if (name === 'mentee_satisfaction') return [`${value.toFixed(1)}%`, 'Mentee Satisfaction'];
                if (name === 'workshop_count') return [`${value} workshops`, 'Workshop Count'];
                if (name === 'active_mentees') return [`${value} mentees`, 'Active Mentees'];
                if (name === 'effectiveness_score') return [`${value.toFixed(1)}%`, 'Effectiveness Score'];
                return [value, name];
              }}
              labelFormatter={(label, payload) => {
                if (payload && payload[0]) {
                  return `Mentor: ${payload[0].payload.mentor_name}`;
                }
                return '';
              }}
            />
            <Scatter dataKey="success_rate" fill="#8884d8">
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getMentorColor(index)} 
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Mentor Performance</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => b.effectiveness_score - a.effectiveness_score)
              .map((item, index) => {
                const effectiveness = getEffectivenessLevel(item.effectiveness_score);
                return (
                  <div key={`${item.mentor_name}-${index}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: getMentorColor(index) }}
                      ></div>
                      <span className="text-sm font-medium">{item.mentor_name}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.active_mentees}</span> mentees
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: effectiveness.color + '20', 
                        color: effectiveness.color 
                      }}>
                        {effectiveness.level}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Effectiveness Insights</h4>
          <div className="space-y-3">
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Most Effective</span>
                <span className="font-bold text-green-600">
                  {data.reduce((max, item) => item.effectiveness_score > max.effectiveness_score ? item : max, data[0])?.mentor_name}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.max(...data.map(d => d.effectiveness_score)).toFixed(1)}% effectiveness
              </div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Highest Workload</span>
                <span className="font-bold text-blue-600">
                  {data.reduce((max, item) => item.workload_score > max.workload_score ? item : max, data[0])?.mentor_name}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...data.map(d => d.workload_score)).toFixed(1)}% workload
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-purple-600">Best Satisfaction</span>
                <span className="font-bold text-purple-600">
                  {data.reduce((max, item) => item.mentee_satisfaction > max.mentee_satisfaction ? item : max, data[0])?.mentor_name}
                </span>
              </div>
              <div className="text-xs text-purple-500 mt-1">
                {Math.max(...data.map(d => d.mentee_satisfaction)).toFixed(1)}% satisfaction
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorEffectivenessAnalysisChart;
